import { useEffect, useRef } from "react";
import { getSharedAudioContext } from "../AudioLevelMeter";
import { SpeakingDetector, rms } from "./videoPolicy";
import { UPLOAD_LOG_KEY, outboundBytes, uploadRow, uploadTotal, type OutboundBytes, type UploadRow } from "./uploadStats";

/**
 * Tells the hub when the local microphone is speaking (FPT-7), so it can rotate this person into the video
 * set. Measured on the track actually being sent (the noise-suppressed one when suppression is on), every
 * 100 ms; a muted microphone is silent here too, so muting stops the reports. At most one report a second
 * while speaking (`SpeakingDetector`), and the hub rate-limits again.
 */
export function useSpeakingReporter(track: MediaStreamTrack | null, enabled: boolean, report: () => void): void {
    const reportRef = useRef(report);
    reportRef.current = report;

    useEffect(() => {
        if (!enabled || !track || track.readyState !== "live") return;
        let ctx: AudioContext;
        let source: MediaStreamAudioSourceNode;
        let analyser: AnalyserNode;
        try {
            ctx = getSharedAudioContext();
            source = ctx.createMediaStreamSource(new MediaStream([track]));
            analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);
        } catch {
            return; // No Web Audio: the person still rotates in as a newest joiner, just not by speaking.
        }
        const buf = new Float32Array(analyser.fftSize);
        const detector = new SpeakingDetector();
        const handle = window.setInterval(() => {
            if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
            analyser.getFloatTimeDomainData(buf);
            // A disabled (muted) track feeds zeros; check anyway in case a browser keeps the last samples.
            const level = track.enabled ? rms(buf) : 0;
            if (detector.feed(level, performance.now())) reportRef.current();
        }, 100);
        return () => {
            window.clearInterval(handle);
            try { source.disconnect(); } catch { /* already gone */ }
        };
    }, [track, enabled]);
}

declare global {
    interface Window {
        /** Latest upload sample while the upload log is on (see uploadStats.ts). */
        __fgMeetingUpload?: { at: string; rows: UploadRow[]; total: UploadRow };
    }
}

function uploadLogOn(): boolean {
    try {
        return window.localStorage.getItem(UPLOAD_LOG_KEY) === "1";
    } catch {
        return false;
    }
}

/**
 * Developer measurement (FPT-7): with `localStorage["fg.connect.meeting.uploadLog"] = "1"`, logs what this
 * browser uploads to each peer and in total every 5 s (console.table) and keeps the latest sample on
 * `window.__fgMeetingUpload`. Off by default; costs nothing when off.
 */
export function useUploadLog(getPeers: () => Record<string, RTCPeerConnection>, getLabels: () => Record<string, string>): void {
    const getPeersRef = useRef(getPeers);
    getPeersRef.current = getPeers;
    const getLabelsRef = useRef(getLabels);
    getLabelsRef.current = getLabels;

    useEffect(() => {
        if (!uploadLogOn()) return;
        let cancelled = false;
        const prev = new WeakMap<RTCPeerConnection, { at: number; bytes: OutboundBytes }>();
        const tick = async () => {
            const labels = getLabelsRef.current();
            const rows: UploadRow[] = [];
            for (const [id, pc] of Object.entries(getPeersRef.current())) {
                if (pc.connectionState === "closed") continue;
                try {
                    const report = await pc.getStats();
                    const bytes = outboundBytes(report.values() as Iterable<Record<string, unknown>>);
                    const now = performance.now();
                    const last = prev.get(pc);
                    prev.set(pc, { at: now, bytes });
                    rows.push(uploadRow(labels[id] || id.slice(0, 8), last?.bytes, bytes, last ? (now - last.at) / 1000 : 0));
                } catch { /* closing */ }
            }
            if (cancelled) return;
            const total = uploadTotal(rows);
            window.__fgMeetingUpload = { at: new Date().toISOString(), rows, total };
            console.table([...rows, total]);
        };
        void tick();
        const handle = window.setInterval(() => void tick(), 5000);
        return () => {
            cancelled = true;
            window.clearInterval(handle);
        };
    }, []);
}
